import { route } from '@/ziggy-shim';
import { api, apiConfig, CastValue } from './LaravelClient';

export default {
  async index(projectId: Id, data: ApiRequests['api.v1.variation-orders.index']) {
    return await api(route('api.v1.variation-orders.index', projectId), 'get', {
      data,
      casts: {
        next_action_assignee_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        search: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        status: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async claimIndex(projectId: Id) {
    return await api(route('api.v1.variation-orders.claim-index', projectId), 'get');
  },

  async show(id: Id) {
    return await api(route('api.v1.variation-orders.show', id), 'get');
  },

  async accept(id: Id) {
    return await api(route('api.v1.variation-orders.accept', id), 'post');
  },

  async principle(id: Id) {
    return await api(route('api.v1.variation-orders.approve-in-principle', id), 'post');
  },

  async decline(id: Id) {
    return await api(route('api.v1.variation-orders.decline', id), 'post');
  },

  async price(id: Id, data: ApiRequests['api.v1.variation-orders.pricing']) {
    return await api(route('api.v1.variation-orders.pricing', id), 'post', {
      data,
    });
  },

  async reprice(id: Id, data: ApiRequests['api.v1.variation-orders.reprice']) {
    return await api(route('api.v1.variation-orders.reprice', id), 'post', {
      data,
    });
  },

  async submit(id: Id, data: ApiRequests['api.v1.variation-orders.submit']) {
    return await api(route('api.v1.variation-orders.submit', id), 'post', {
      data,
      casts: {
        description: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async complete(id: Id) {
    return await api(route('api.v1.variation-orders.complete', id), 'post');
  },

  async transfer(projectId: Id, id: Id, data: ApiRequests['api.v1.variation-orders.transfer']) {
    return await api(route('api.v1.variation-orders.transfer', [projectId, id]), 'post', {
      data,
    });
  },

  async provision(id: Id, signedParams: unknown) {
    return await apiConfig(route('api.v1.variation-orders.provision', id), 'post', {
      params: signedParams,
      paramsSerializer: {
        serialize: (params) => {
          return new URLSearchParams(params).toString();
        },
      },
    });
  },

  async declineSubmission(
    id: Id,
    signedParams: unknown,
    data: ApiRequests['api.v1.variation-orders.decline-submission'],
  ) {
    return await apiConfig(route('api.v1.variation-orders.decline-submission', id), 'post', {
      params: signedParams,
      paramsSerializer: {
        serialize: (params) => {
          return new URLSearchParams(params).toString();
        },
      },
    }, {
      data,
    });
  },

  async approve(id: Id, data: ApiRequests['api.v1.variation-orders.approve']) {
    return await api(route('api.v1.variation-orders.approve', id), 'post', {
      data,
    });
  },

  async create(projectId: Id, data: ApiRequests['api.v1.variation-orders.store'] | FormData) {
    return await api(route('api.v1.variation-orders.store', projectId), 'post', {
      data,
      casts: {
        head_contract_trade_item_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        notes: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        client_ref: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async update(id: Id, data: ApiRequests['api.v1.variation-orders.update']) {
    return await api(route('api.v1.variation-orders.update', id), 'patch', {
      data,
      casts: {
        notes: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        head_contract_trade_item_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        package_trade_item_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        client_ref: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        head_contract_variation_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        variation_order_category_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async nextAction(id: Id, data: ApiRequests['api.v1.variation-orders.next-action']) {
    return await api(route('api.v1.variation-orders.next-action', id), 'patch', {
      data,
      casts: {
        next_action: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        next_action_assignee_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        next_action_due_date: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async updateForecastAmounts(id: Id, data: ApiRequests['api.v1.variation-orders.forecast-amounts']) {
    return await api(route('api.v1.variation-orders.forecast-amounts', id), 'patch', {
      data,
    });
  },

  async updateBaselineForecast(id: Id, data: ApiRequests['api.v1.variation-orders.baseline-forecast']) {
    return await api(route('api.v1.variation-orders.baseline-forecast', id), 'patch', {
      data,
    });
  },

  async delete(id: Id) {
    return await api(route('api.v1.variation-orders.destroy', id), 'delete');
  },

  async summary(projectid: Id) {
    return await api(route('api.v1.variation-orders.summary', projectid), 'get');
  },

  async summaryReport(projectId: Id, data: ApiRequests['api.v1.variation-orders.summary-report']) {
    return await api(route('api.v1.variation-orders.summary-report', projectId), 'post', {
      data,
    });
  },

  async comment(id: Id, data: ApiRequests['api.v1.variation-orders.comments']) {
    return await api(route('api.v1.variation-orders.comments', id), 'post', {
      data,
    });
  },

  async sendNotice(id: Id, data: ApiRequests['api.v1.variation-orders.send-notice']) {
    return await api(route('api.v1.variation-orders.send-notice', id), 'post', {
      data,
      casts: {
        description: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async media(id: Id) {
    return await api(route('api.v1.variation-orders.media.show', id), 'get');
  },

  async storeMedia(id: Id, data: ApiRequests['api.v1.variation-orders.media.store'] | FormData) {
    return await api(route('api.v1.variation-orders.media.store', id), 'post', {
      data,
    });
  },

  notice: {
    async store(id: Id, data: ApiRequests['api.v1.variation-orders.notice.store'] | FormData) {
      return await api(route('api.v1.variation-orders.notice.store', id), 'post', {
        data,
      });
    },
  },

  pricingNotice: {
    async store(id: Id, data: ApiRequests['api.v1.variation-orders.pricing-notice.store'] | FormData) {
      return await api(route('api.v1.variation-orders.pricing-notice.store', id), 'post', {
        data,
      });
    },
  },

  correspondence: {
    async index(id: Id) {
      return await api(route('api.v1.variation-orders.correspondence.index', id), 'get');
    },

    async store(id: Id, data: ApiRequests['api.v1.variation-orders.correspondence.store'] | FormData) {
      return await api(route('api.v1.variation-orders.correspondence.store', id), 'post', {
        data,
      });
    },
  },

  subcontract: {
    async index(projectId: Id) {
      return await api(route('api.v1.package-variation-orders.index', projectId), 'get');
    },
  },

  admin: {
    async index(data: ApiRequests['api.v1.admin.variation-orders.index'] & { page?: number } = {}) {
      return await api(route('api.v1.admin.variation-orders.index'), 'get', {
        data,
        casts: {
          project_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async show(id: Id) {
      return await api(route('api.v1.admin.variation-orders.show', id), 'get');
    },

    async update(id: Id, data: ApiRequests['api.v1.admin.variation-orders.update']) {
      return await api(route('api.v1.admin.variation-orders.update', id), 'patch', {
        data,
      });
    },

    async delete(id: Id) {
      return await api(route('api.v1.admin.variation-orders.destroy', id), 'delete');
    },
  },

  package: {
    async index(projectId: Id, packageId: Id, data: ApiRequests['api.v1.packages.variation-orders.index']) {
      return await api(route('api.v1.packages.variation-orders.index', [projectId, packageId]), 'get', {
        data,
        casts: {
          next_action_assignee_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
          search: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
          status: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async approvedIndex(projectId: Id, packageId: Id) {
      return await api(route('api.v1.packages.approved-variations.index', [projectId, packageId]), 'get');
    },

    async store(
      projectId: Id,
      packageId: Id,
      data: ApiRequests['api.v1.packages.variation-orders.store'] | FormData,
    ) {
      return await api(route('api.v1.packages.variation-orders.store', [projectId, packageId]), 'post', {
        data,
        casts: {
          package_trade_item_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
          notes: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
          client_ref: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async backCharge(
      projectId: Id,
      packageId: Id,
      variationOrderId: Id,
      data: ApiRequests['api.v1.packages.variation-orders.back-charge'],
    ) {
      return await api(
        route('api.v1.packages.variation-orders.back-charge', [projectId, packageId, variationOrderId]),
        'post',
        {
          data,
        },
      );
    },
  },
};
